import { useGetStudentOverviewResultQuery } from "util/rtk-api"

import { SECTIONS, SECTION_SHORT_HEADERS, normalizeSection } from "@all-for-one/shared-types"
import { StudentContext } from "contexts/student-context"
import styles from 'css/components/tables/student-table.module.css'
import { useContext } from "react"
import { useNavigate, Link } from "react-router-dom"

import { GenericTable, NumberedView } from "./generic-table"

export const StudentOverviewTable = () => {
    const { classId, studentId } = useContext(StudentContext)
    const navigate = useNavigate()

    const { data: studentOverviewResults, isLoading } = useGetStudentOverviewResultQuery({
        classId,
        studentId,
    })

    const flattenSectionResults = studentOverviewResults?.map(result => {
        const sectionScores: Record<string, number> = {}
        for (const { section, score } of (result.sectionScores ?? [])) {
            // Normalize section names to match SECTIONS enum (EduQuery uses 'Pre-Assessment', code uses 'pre_assessment')
            const normalizedSection = normalizeSection(section)
            sectionScores[normalizedSection] = score
        }
        return {
            ...result,
            ...sectionScores
        }
    })?.sort((a, b) => {
        // Use grading timestamp if available, fall back to lesson lastModified (epoch millis)
        const tsA = a.lastGradingAttempt ? new Date(a.lastGradingAttempt).getTime()
            : a.submittedAt ? new Date(a.submittedAt).getTime()
            : a.lessonLastModified || 0
        const tsB = b.lastGradingAttempt ? new Date(b.lastGradingAttempt).getTime()
            : b.submittedAt ? new Date(b.submittedAt).getTime()
            : b.lessonLastModified || 0
        return tsB - tsA
    })

    const columns = [
        {
            key: 'lessonName', header: "Lesson", render: (lessonName, index, row) =>
            <Link
                className={styles.lessonLink}
                to={`/classrooms/${classId}/students/${studentId}/lessons/${row.lessonId}`}
                >
                <NumberedView index={index} value={lessonName} />
            </Link>
        },
        { key: 'difficulty', header: "Level", render: (difficulty) => difficulty ?? '-' },
        { key: 'lastGradingAttempt', header: "Date Graded", render: (val, _idx, row) => {
            const dateStr = val || row.submittedAt
            if (!dateStr) return '-'
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        }},
        { key: 'overallScore', header: "Overall %", render: (score) => score != null ? Number(score).toFixed(1) : '-' },
        { key: SECTIONS.PRE_ASSESSMENT, header: SECTION_SHORT_HEADERS[SECTIONS.PRE_ASSESSMENT], render: (score) => score != null ? Number(score).toFixed(1) : '-' },
        { key: SECTIONS.CHECK_FOR_UNDERSTANDING, header: SECTION_SHORT_HEADERS[SECTIONS.CHECK_FOR_UNDERSTANDING], render: (score) => score != null ? Number(score).toFixed(1) : '-' },
        { key: SECTIONS.INDEPENDENT_WORK, header: SECTION_SHORT_HEADERS[SECTIONS.INDEPENDENT_WORK], render: (score) => score != null ? Number(score).toFixed(1) : '-' },
        { key: SECTIONS.POST_ASSESSMENT, header: SECTION_SHORT_HEADERS[SECTIONS.POST_ASSESSMENT], render: (score) => score != null ? Number(score).toFixed(1) : '-' },
    ]

    return (
        flattenSectionResults ? 
        <GenericTable
            columns={columns}
            data={flattenSectionResults}
            rowKey="lessonId"
            rowClassName={(row, idx) => idx % 2 === 0 ? '' : styles.highlightRow}
            style={{}}
            renderHeader={true}
            renderMoreActions={false}
            doubleClickRowAction={(lessonId) => navigate(`/classrooms/${classId}/students/${studentId}/lessons/${lessonId}`)}
        />
    : <div>Loading...</div>
)}